import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useRBACContext } from '@providers/RBACContext';
import { useFlags, useUser } from '@hooks';
import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk';
import { mapFieldsBack } from '@utils';
import {
  useGetNBCUAdvertiser,
  usePutNBCUAdvertiser,
} from '@apis/advertisers';

import {
  AdminFieldMappings,
  AdminReverseFieldMappings,
  mapAdminFields,
} from './helpers';
import { TabValues } from './constants';
import { AdAccountUsersList } from './AdAccountUsersList';
import { AdminForm } from './AdminForm';
import { GeneralInfoForm } from '../GeneralInfoForm';
import { OrgChips } from '../../OrgChips';
import { ReportingForm } from '../ReportingForm';
import { SupportTab } from './SupportTab';
import { useAPI } from '../../hooks/api';
import { useLoader } from '../../hooks/loader';
import ReportsAdmin from './ReportsAdmin';

const PREFIX = 'ManageAdAccount';

const classes = {
  active: `${PREFIX}-active`,
  admin: `${PREFIX}-admin`,
  card: `${PREFIX}-card`,
  selectedCard: `${PREFIX}-selectedCard`,
  cardContent: `${PREFIX}-cardContent`,
  cardLabel: `${PREFIX}-cardLabel`,
  cart: `${PREFIX}-cart`,
  chip: `${PREFIX}-chip`,
  download: `${PREFIX}-download`,
  dropdown: `${PREFIX}-dropdown`,
  emptyChannels: `${PREFIX}-emptyChannels`,
  general: `${PREFIX}-general`,
  submit: `${PREFIX}-submit`,
  successMsg: `${PREFIX}-successMsg`,
  supportAccess: `${PREFIX}-supportAccess`,
  tab: `${PREFIX}-tab`,
  textfield: `${PREFIX}-textfield`
};

const StyledBox = styled(Box)(({
  theme: { palette, spacing, typography }
}) => ({
  [`& .${classes.active}`]: {
    backgroundColor: '#c2f1e3',
    color: '#05734e',
  },

  [`& .${classes.admin}`]: {
    backgroundColor: '#bfe7ff',
    color: '#2592cf',
  },

  [`& .${classes.card}`]: {
    width: 150,
    height: 150,
    cursor: 'pointer',

    '&:hover': {
      boxShadow:
        '0px 5px 5px -3px rgb(29 175 255 / 20%), 0px 8px 10px 1px rgb(29 175 255 / 14%), 0px 3px 14px 2px rgb(29 175 255 / 12%)',
    },
  },

  [`& .${classes.selectedCard}`]: {
    backgroundColor: 'rgba(29, 175, 255, 0.2)',

    '&:hover': {
      boxShadow:
        '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    },
  },

  [`& .${classes.cardContent}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    paddingBottom: '16px !important',
  },

  [`& .${classes.cardLabel}`]: {
    marginTop: spacing(1),
    textAlign: 'center',
  },

  [`& .${classes.cart}`]: {
    width: 68,
    height: 58,
    margin: '0 auto',
  },

  [`& .${classes.chip}`]: {
    marginLeft: spacing(2),
    padding: spacing(1),
  },

  [`& .${classes.download}`]: {
    width: 35,
    height: 58,
    margin: '0 auto',
  },

  [`& .${classes.dropdown}`]: {
    width: '48%',
  },

  [`& .${classes.emptyChannels}`]: {
    height: 120,

    ['& td']: {
      borderBottom: 'none',
    },

    ['& td p']: {
      color: '#67747c',
    },
  },

  [`& .${classes.general}`]: {
    width: 58,
    height: 58,
    margin: '0 auto',
  },

  [`& .${classes.submit}`]: {
    ...typography.h4,
    color: '#fff',
    marginLeft: spacing(2),
    padding: spacing(2),
    width: 175,
  },

  [`& .${classes.successMsg}`]: {
    color: palette.primary.light,
  },

  [`& .${classes.supportAccess}`]: {
    color: '#67747c',
  },

  [`& .${classes.tab}`]: {
    ['& h4']: {
      fontWeight: 500,
    },

    ['&.Mui-selected h4']: {
      fontWeight: 700,
    },
  },

  [`& .${classes.textfield}`]: {
    marginBottom: spacing(3),
  }
}));

const TabPanel = props => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`horizontal-tabpanel-${index}`}
      aria-labelledby={`horizontal-tab-${index}`}
      style={{ width: '100%', height: '100%' }}
      {...other}
    >
      {value === index && (
        <Box p={2} height="100%">
          {children}
        </Box>
      )}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const ManageAdAccount = ({
  org,
  currentAccount,
  onCancel,
  categories,
}) => {
  const { flags } = useFlags();
  const { user } = useUser();
  const { permissions } = useRBACContext();
  const { useGetAll, useGet } = useAPI();
  const { isLoading, setIsLoading } = useLoader(true);

  const canViewNBCUAdvertiser = useMemo(
    () => permissions.CAN_VIEW_NBCU_ADVERTISER,
    [flags, permissions]
  );

  const { data: nbcuAdvertiser = {} } = useGetNBCUAdvertiser(canViewNBCUAdvertiser ? currentAccount.ad_account_id : null);
  const { trigger: putNBCUAdvertiser } = usePutNBCUAdvertiser(canViewNBCUAdvertiser ? currentAccount.ad_account_id : null);

  const [tabValue, setTabValue] = useState(TabValues.GENERAL_INFO);
  const [advertiser, setAdvertiser] = useState(null);
  const [billingAccounts, setBillingAccounts] = useState([]);

  const adminFormFields = useMemo(
    () => mapAdminFields(nbcuAdvertiser),
    [nbcuAdvertiser],
  );

  const { reportsAdmin } = useLDFlags();

  const onAdminFormSubmit = useCallback(async (values, setError) => {
    try {
      const data = mapFieldsBack(values, AdminFieldMappings);

      await putNBCUAdvertiser(data);
    } catch (error) {
      const { data: errors } = error.response;

      Object.keys(errors).forEach(key => {
        if (errors[key] instanceof Array) {
          setError(AdminReverseFieldMappings[key], {
            type: "server",
            message: errors[key][0],
          });
        }
      });
    }
  }, []);

  const handleTabChange = (event, value) => {
    setTabValue(value);
  };

  useEffect(() => {
    if (!currentAccount.advertiser) return;

    useGet(currentAccount.advertiser).then(response => {
      setAdvertiser(response);
    });
  }, [currentAccount.advertiser]);

  useEffect(() => {
    useGetAll('/billing_accounts', [], results => {
      setBillingAccounts(results)
      setIsLoading(false);
    });
  }, []);

  return (
    <StyledBox
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      height="100%"
    >
      <Box mb={6}>
        <Box mb={4}>
          <Box
            mb={4}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box width="50%">
              <Button
                startIcon={<ArrowBackIcon color="secondary" />}
                onClick={onCancel}
              >
                <Typography>{advertiser?.name}</Typography>
              </Button>
            </Box>

            <OrgChips org={org} />
          </Box>

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Business Manager Tabs"
            className={classes.tabs}
          >
            <Tab
              className={classes.tab}
              label={<Typography variant="h4">General Info</Typography>}
              value={TabValues.GENERAL_INFO}
            />
            <Tab
              className={classes.tab}
              label={<Typography variant="h4">Ad Account Users</Typography>}
              value={TabValues.AD_ACCOUNT_USERS}
            />
            <Tab
              className={classes.tab}
              label={<Typography variant="h4">Reporting</Typography>}
              value={TabValues.REPORTING}
            />
            {reportsAdmin && user.is_tvsci_employee && (
              <Tab
                className={classes.tab}
                label={<Typography variant="h4">Admin</Typography>}
                value={TabValues.REPORT_AVAILABILITY}
              />
            )}
            {canViewNBCUAdvertiser && (
              <Tab
                className={classes.tab}
                label={<Typography variant="h4">NBCU Admin</Typography>}
                value={TabValues.ADMIN}
              />
            )}
          </Tabs>
        </Box>

        <Box height="100%">
          {tabValue === TabValues.GENERAL_INFO && (
            <GeneralInfoForm
              isLoading={isLoading}
              advertiser={advertiser}
              setAdvertiser={setAdvertiser}
              categories={categories}
              billingAccounts={billingAccounts}
              onClose={onCancel}
            />
          )}

          {tabValue === TabValues.AD_ACCOUNT_USERS && (
            <AdAccountUsersList currentAccount={currentAccount} />
          )}

          {tabValue === TabValues.REPORTING && (
            <ReportingForm
              advertiser={advertiser}
              setAdvertiser={setAdvertiser}
            />
          )}

          {tabValue === TabValues.SUPPORT && (
            <SupportTab />
          )}

          {tabValue === TabValues.ADMIN && (
            <AdminForm
              data={adminFormFields}
              onSubmit={onAdminFormSubmit}
            />
          )}

          {tabValue === TabValues.REPORT_AVAILABILITY && reportsAdmin && (
            <ReportsAdmin currentAccount={currentAccount} />
          )}
        </Box>
      </Box>
    </StyledBox>
  );
};

ManageAdAccount.propTypes = {
  org: PropTypes.object,
  currentAccount: PropTypes.object,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  categories: PropTypes.array,
};

export default ManageAdAccount;
